import { ResourceContent as RawResourceContent } from '@protos/content_management/content.common.apis_pb';
import {
  PageContentInfo,
  ResourceContent,
} from '@protos/content_management/content.db_pb';
import { QAttemptResultEnum } from '@protos/geneo_ai/ai.student.events_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssessmentStudentResponse,
  StudentAssessAttemptResult,
  StudentAssessCountInfo,
  StudentAssessmentTask,
} from '@protos/learning_management/lms.assessment.student.apis_pb';
import { VisitedResourceContentInfoList } from '@protos/learning_management/lms.db_pb';
import { LessonContent } from '@protos/learning_management/lms.lesson.common.apis_pb';
import { StudentPreviousProjectSession } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { StudentPreviousLessonSession } from '@protos/learning_management/lms.lesson.learn.apis_pb';
import { createSlice, PayloadAction, Slice } from '@reduxjs/toolkit';

interface SelectedResourceInfo {
  pageNumber?: number;
  resourceId?: string;
  pageContent?: PageContentInfo[];
  resourceAnswerStatus: QAttemptResultEnum;
  currentAnswer: string[];
}

export interface ResourceSessionOwner {
  studentId: string;
  resourceId: string;
  projectSessionId: string;
}

export interface ILaboratoryState {
  selected_module_id: string;
  home_homework_list?: StudentAssessmentTask[];
  ended_homework_list?: StudentAssessmentTask[];
  active_homework_Id?: string;
  active_homework_content?: {
    [homeworkId: string]: AssessmentContent | undefined;
  };
  active_homework_student_response?: {
    [homeworkId: string]: AssessmentStudentResponse | undefined;
  };
  active_homework_attempt_result?: {
    [homeworkId: string]: StudentAssessAttemptResult | undefined;
  };
  project_end_path?: string;
  active_homework_session_id?: number;
  selected_homework_data?: {
    homeworkId: number;
    questionIds: string[];
    resumeCheck: boolean;
    reattemptedCheck: boolean;
    SubmissionType: number;
    elapsedTime?: number;
  };
  assigned_homework?: { [pageNumber: number]: StudentAssessmentTask[] };
  ended_homework?: { [pageNumber: number]: StudentAssessmentTask[] };
  hw_info?: {
    hwListInfo: StudentAssessCountInfo | undefined;
    totalAssignedPages: number;
    totalEndedPages: number;
  };
  all_answers: string[];
  selected_lesson_content?: LessonContent;
  previous_lesson_session_info?: StudentPreviousLessonSession;
  selected_resource_content?: {
    rawData: RawResourceContent;
    parsedData: ResourceContent;
  };
  selected_resource_session_id?: bigint;
  //~ Who the stored resource session belongs to. The store outlives a logout,
  //~ so the id is only reused when all three still match.
  selected_resource_session_owner?: ResourceSessionOwner;
  active_project_session_id?: bigint;
  previous_project_session_info?: StudentPreviousProjectSession;
  lesson_plan_redirection_path?: string;
  resource_redirection_path?: string;
  lesson_session_visited_resource_info?: {
    [lessonSessionId: string]: VisitedResourceContentInfoList[];
  };
  selected_resource_info?: SelectedResourceInfo;
}

const initialState: ILaboratoryState = {
  selected_module_id: 'abcd',
  home_homework_list: [],
  ended_homework_list: [],
  active_homework_content: undefined,
  active_homework_student_response: undefined,
  active_homework_attempt_result: undefined,
  active_homework_Id: undefined,
  project_end_path: undefined,
  selected_homework_data: undefined,
  assigned_homework: {},
  ended_homework: {},
  hw_info: {
    hwListInfo: undefined,
    totalAssignedPages: 1,
    totalEndedPages: 1,
  },
  all_answers: [],
  selected_lesson_content: undefined,
  previous_lesson_session_info: undefined,
  selected_resource_content: undefined,
  selected_resource_session_id: undefined,
  selected_resource_session_owner: undefined,
  active_project_session_id: undefined,
  previous_project_session_info: undefined,
  lesson_plan_redirection_path: undefined,
  resource_redirection_path: undefined,
  selected_resource_info: undefined,
};

export const laboratory: Slice<ILaboratoryState> = createSlice({
  name: 'laboratory',
  initialState,
  reducers: {
    setActiveHomeworkList: (state, action) => {
      state.home_homework_list = action.payload;
    },
    setEndedHomeworkList: (state, action) => {
      state.ended_homework_list = action.payload;
    },
    setActiveHomeworkId: (state, action) => {
      state.active_homework_Id = action.payload;
    },
    setActiveHomeworkContent: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        homeworkContent?: AssessmentContent;
      }>
    ) => {
      state.active_homework_content = {
        ...state.active_homework_content,
        [action.payload.homeworkId.toString()]: action.payload.homeworkContent,
      };
    },
    setActiveHomeworkStudentResponse: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        response?: AssessmentStudentResponse;
      }>
    ) => {
      state.active_homework_student_response = {
        ...state.active_homework_student_response,
        [action.payload.homeworkId.toString()]: action.payload.response,
      };
    },
    setActiveHomeworSessionId: (state, action) => {
      state.active_homework_session_id = action.payload;
    },
    setActiveHomeworkAttemptResult: (
      state,
      action: PayloadAction<{
        homeworkId: number;
        result?: StudentAssessAttemptResult;
      }>
    ) => {
      state.active_homework_attempt_result = {
        ...state.active_homework_attempt_result,
        [action.payload.homeworkId.toString()]: action.payload.result,
      };
    },
    setProjectEndPath: (state, action: PayloadAction<string | undefined>) => {
      state.project_end_path = action.payload;
    },
    setSelectedHomeworkData: (state, action) => {
      state.selected_homework_data = action.payload;
    },
    setAssignedData: (state, action) => {
      state.assigned_homework = action.payload;
    },
    setEndedData: (state, action) => {
      state.ended_homework = action.payload;
    },
    setHwInfoData: (state, action) => {
      state.hw_info = action.payload;
    },

    setAllAnswers: (state, action) => {
      state.all_answers = action.payload;
    },
    setSelectedLessonContent: (state, action) => {
      state.selected_lesson_content = action.payload;
    },
    setLessonPreviousSessionInfo: (state, action) => {
      state.previous_lesson_session_info = action.payload;
    },
    setSelectedResourceContent: (state, action) => {
      state.selected_resource_content = action.payload;
    },
    setSelectedResourceSessionId: (
      state,
      action: PayloadAction<{
        sessionId?: bigint;
        owner?: ResourceSessionOwner;
      }>
    ) => {
      state.selected_resource_session_id = action.payload.sessionId;
      state.selected_resource_session_owner = action.payload.sessionId
        ? action.payload.owner
        : undefined;
    },
    setActiveProjectSessionId: (
      state,
      action: PayloadAction<bigint | undefined>
    ) => {
      state.active_project_session_id = action.payload;
    },
    setPreviousProjectSessionInfo: (
      state,
      action: PayloadAction<StudentPreviousProjectSession | undefined>
    ) => {
      state.previous_project_session_info = action.payload;
    },
    setLessonPlanRedirectionPath: (state, action) => {
      state.lesson_plan_redirection_path = action.payload;
    },
    setResourceRedirectionPath: (state, action) => {
      state.resource_redirection_path = action.payload;
    },
    setUpdatedLessonSessionVisitedResourceInfo: (
      state,
      action: PayloadAction<{
        [lessonSessionId: string]: VisitedResourceContentInfoList[];
      }>
    ) => {
      state.lesson_session_visited_resource_info = action.payload;
    },
    setSelectedResourceInfo: (state, action) => {
      state.selected_resource_info = action.payload;
    },
    resetHomeworkState: (state) => {
      return initialState;
    },
  },
});

export const {
  setActiveHomeworkList,
  setEndedHomeworkList,
  setActiveHomeworkId,
  setActiveHomeworkContent,
  setActiveHomeworkStudentResponse,
  setActiveHomeworkAttemptResult,
  resetHomeworkState,
  setProjectEndPath,
  setActiveHomeworSessionId,
  setSelectedHomeworkData,
  setAssignedData,
  setEndedData,
  setHwInfoData,
  setAllAnswers,
  setSelectedLessonContent,
  setLessonPreviousSessionInfo,
  setSelectedResourceContent,
  setSelectedResourceSessionId,
  setActiveProjectSessionId,
  setPreviousProjectSessionInfo,
  setLessonPlanRedirectionPath,
  setUpdatedLessonSessionVisitedResourceInfo,
  setResourceRedirectionPath,
  setSelectedResourceInfo,
} = laboratory.actions;

export default laboratory.reducer;
